// The board's state in the address bar, so a view can be shared as a link.
//
// Only what differs from the default is written, so the plain address is the default board.

import { DEFAULT_FILTERS, LEGACY_TABS, REASONS, SORT_KEYS, TABS } from './view.ts';
import type { Filters, Reason, Sort, SortKey, Tab } from './view.ts';

export interface BoardState {
  filters: Filters;
  sort: Sort | null;
  open_id: string | null;
}

const PERIOD = /^\d{4}-(0[1-9]|1[0-2]|Q[1-4])$/;

// A current tab key, else an older one's replacement (`open_enterprise` opens New Business), else
// the default.
function parseTab(value: string | null): Tab {
  const current = TABS.find(tab => tab === value);
  if (current) return current;
  return LEGACY_TABS.get(value ?? '') ?? DEFAULT_FILTERS.tab;
}

function parseStage(value: string | null): number | null {
  const stage = Number(value);
  return Number.isInteger(stage) && stage >= 1 && stage <= 5 ? stage : null;
}

function parseReasons(value: string | null): Reason[] {
  if (!value) return [];
  const picked = value.split(',');
  return REASONS.filter(reason => picked.includes(reason));
}

function parsePeriod(value: string | null): string | null {
  return value && PERIOD.test(value) ? value : null;
}

function parseSort(value: string | null): Sort | null {
  if (!value) return null;
  const [key, direction] = value.split(':');
  const sortKey = SORT_KEYS.find(item => item === key) as SortKey | undefined;
  if (!sortKey || (direction !== 'asc' && direction !== 'desc')) return null;
  return { key: sortKey, direction };
}

export function readState(search: string): BoardState {
  const params = new URLSearchParams(search);
  return {
    filters: {
      tab: parseTab(params.get('tab')),
      rep_id: params.get('rep') || null,
      stage: parseStage(params.get('stage')),
      reasons: parseReasons(params.get('reasons')),
      search: params.get('q') ?? '',
      created: parsePeriod(params.get('created')),
      closing: parsePeriod(params.get('close')),
    },
    sort: parseSort(params.get('sort')),
    open_id: params.get('opp') || null,
  };
}

export function writeState(state: BoardState): string {
  const { filters, sort, open_id: openId } = state;
  const params = new URLSearchParams();
  if (filters.tab !== DEFAULT_FILTERS.tab) params.set('tab', filters.tab);
  if (filters.rep_id) params.set('rep', filters.rep_id);
  if (filters.stage !== null) params.set('stage', String(filters.stage));
  if (filters.reasons.length > 0) params.set('reasons', filters.reasons.join(','));
  if (filters.search.trim()) params.set('q', filters.search.trim());
  if (filters.created) params.set('created', filters.created);
  if (filters.closing) params.set('close', filters.closing);
  if (sort) params.set('sort', `${sort.key}:${sort.direction}`);
  if (openId) params.set('opp', openId);
  const search = params.toString();
  return search ? `?${search}` : '';
}
